import React from 'react';

import { useMenuItemDragAndDrop } from '@atlaskit/navigation-system/side-nav-items/drag-and-drop/use-menu-item-drag-and-drop';

import { useElementRegistry } from '../../../../controllers/element-registry';
import { useOptions } from '../../../../controllers/options-context';
import type { NavItemConfigInternal } from '../../../types';
import { getL1ItemData, isL1ItemData } from '../../../utils/dnd-data';
import { getDragPreviewContentForItem, getElemBeforeForItem } from '../get-drag-preview';

interface ItemDragAndDrop {
	draggableRef: React.RefCallback<HTMLAnchorElement | HTMLButtonElement> | undefined;
	dropIndicator: React.ReactNode | undefined;
	dropTargetRef: React.RefObject<HTMLDivElement> | undefined;
	isDragging: boolean;
	isItemDraggable: boolean;
	maybeDragPreview: React.ReactNode;
}

export function useItemDragAndDrop({
	item,
	id,
	isExpandable,
	isExpandOrFlyoutOpen,
	isInMoreMenu,
	onExpandFlyoutToggle,
	peek,
}: {
	id: string;
	isExpandable: boolean;
	isExpandOrFlyoutOpen: boolean;
	isInMoreMenu: boolean;
	item: NavItemConfigInternal;
	onExpandFlyoutToggle: (open: boolean) => void;
	peek: boolean;
}): ItemDragAndDrop {
	const { l1DragAndDropEnabled } = useOptions();
	const isItemDraggable =
		l1DragAndDropEnabled &&
		!isInMoreMenu &&
		// TODO: NAVX-1172 Once we support unhiding items using drag and drop, we should remove this check and adjust the
		// logic below to handle dragging a peeked item into the main list to unhide it
		!peek &&
		(item.isHideable !== false || item.isReorderable !== false) &&
		// Custom items must explicitly opt-in to being draggable since they need to consume the refs for it to work
		(item.type !== 'custom' || !!item.isDraggable);

	const {
		draggableAnchorRef,
		draggableButtonRef,
		state: dragState,
		dragPreview,
		dropTargetRef,
		dropIndicator,
	} = useMenuItemDragAndDrop({
		...(isItemDraggable && {
			draggable: {
				getInitialData: () =>
					getL1ItemData({
						menuItemId: id,
						label: item.label,
						isHidden: false,
						isHideable: item.isHideable !== false,
						isReorderable: item.isReorderable !== false,
					}),
				getDragPreviewPieces: () => ({
					elemBefore: getElemBeforeForItem(item),
					content: getDragPreviewContentForItem(item),
				}),
			},
			dropTarget: {
				getData: () =>
					getL1ItemData({
						menuItemId: id,
						label: item.label,
						isHidden: false,
						isHideable: item.isHideable !== false,
						isReorderable: item.isReorderable !== false,
					}),
				getOperations: () => ({
					'reorder-before': 'available',
					'reorder-after': 'available',
				}),
				canDrop: ({ source }) => {
					return (
						isL1ItemData(source.data) && source.data.menuItemId !== id && source.data.isReorderable
					);
				},
			},
		}),
	});

	const registry = useElementRegistry();

	const registrationRef = React.useRef<(() => void) | null>(null);
	const draggableRef = React.useCallback(
		(element: HTMLAnchorElement | HTMLButtonElement | null) => {
			if (!element) {
				registrationRef.current?.();
				registrationRef.current = null;

				draggableAnchorRef.current = null;
				draggableButtonRef.current = null;

				return;
			}

			// Set to appropriate ref from the drag and drop hook
			if (element instanceof HTMLAnchorElement) {
				draggableAnchorRef.current = element;
			} else {
				draggableButtonRef.current = element;
			}

			// Unregister the previous element if it exists
			if (registrationRef.current) {
				registrationRef.current();
			}

			// Register the element with the registry
			registrationRef.current = registry.registerL1Item({
				id,
				element,
			});
		},
		[id, registry, draggableAnchorRef, draggableButtonRef, registrationRef],
	);

	const isDragging = isItemDraggable && dragState.type === 'dragging';
	const expandableClosedForDrag = React.useRef(false);

	React.useLayoutEffect(() => {
		if (!isExpandable || !isItemDraggable) {
			return;
		}
		if (isDragging && isExpandOrFlyoutOpen) {
			// Close the expandable when dragging starts
			onExpandFlyoutToggle(false);

			// Keep track of whether we closed the expandable for drag
			expandableClosedForDrag.current = true;
		} else if (!isDragging && expandableClosedForDrag.current) {
			// If we were dragging and now we're not, reopen the expandable if it was closed
			onExpandFlyoutToggle(true);
			expandableClosedForDrag.current = false;
		}
	}, [
		isDragging,
		isExpandable,
		isExpandOrFlyoutOpen,
		isItemDraggable,
		expandableClosedForDrag,
		onExpandFlyoutToggle,
	]);

	return {
		draggableRef: isItemDraggable ? draggableRef : undefined,
		isItemDraggable,
		isDragging,
		dropTargetRef: isItemDraggable ? dropTargetRef : undefined,
		dropIndicator: isItemDraggable ? dropIndicator : undefined,
		maybeDragPreview: isItemDraggable ? dragPreview : null,
	};
}
